<template>
  <div class="checklist-view">
    <Stack>
      <Stack gap="2">
        <Title>Documents</Title>
        <p>Choose which documents you want to ask your Client to upload.</p>
        <div>
          <Toggle v-model="clientPortalStore.portalRequest.ask_for_documents">
            Ask the client to upload Documents
          </Toggle>
          <YesNoBadge
            :bool="clientPortalStore.portalRequest.ask_for_documents"
            yes-label="Enabled"
            no-label="Disabled"
          />
        </div>
      </Stack>

      <p v-if="hasChecklist && currentChecklist.checklist.name">
        Current Checklist: <strong>{{ currentChecklist.checklist.name }}</strong>
      </p>

      <LoaderWithText v-if="!hasFetched" />


      <template v-else>
        <template
          v-if="!hasChecklist"
        >
          <div
            class="checklist-empty-box"
          >
            <Stack
              class="checklist-empty-body"
              items="center"
              gap="4"
            >
              <Title size="5">
                Your Checklist is empty
              </Title>

              <Buttons>
                <Button
                  variant="primary"
                  icon-after="fas fa-tasks"
                  spacing="loose"
                  size="4_5"
                  @click="showLoaderModal = true"
                >
                  Load Checklist template
                </Button>
              </Buttons>
            </Stack>
          </div>

          <p
            class="or"
          >
            Or configure manually
          </p>
        </template>

        <template
          v-if="allDossierSections.length"
        >
          <Stack
            gap="1"
          >
            <DocumentPortalSection
              v-for="dossierSection in allDossierSections"
              :key="dossierSection.value"
              :dossier-section="dossierSection"
              :current-sections="currentChecklist.sections"
              :has-active-sections="hasChecklist && hasActiveSections"
              @toggle="handleSectionToggle"
              @item-addition="handleItemAddition"
              @item-deletion="handleItemDeletion"
              @item-requirement="handleItemRequirement"
              @new-item="handleItemAddition"
            />
          </Stack>

          <Stack
            class="save-footer"
            gap="2"
            :class="{ 'is-sticky': hasOperations }"
          >
            <Button
              variant="success"
              :loading="isSaving"
              :disabled="!hasOperations"
              spacing="loose"
              @click="bulkUpdateChecklist"
            >
              Save changes
            </Button>
          </Stack>

          <p class="save-footer-disclaimer">
            This won't affect the original template, but will be saved for this client/prospect's checklist.
          </p>

          <template
            v-if="hasChecklist"
          >
            <p class="or">
              Or
            </p>

            <Buttons
              class="stk-py-6"
              justify="center"
            >
              <Button
                icon-before="fas fa-save"
                variant="primary"
                spacing="loose"
                @click="showSaverModal = true"
              >
                Save as template
              </Button>
              <Button
                icon-after="fas fa-tasks"
                spacing="loose"
                @click="showLoaderModal = true"
              >
                Load another template
              </Button>
            </Buttons>
          </template>
        </template>
      </template>
    </Stack>

    <StakeModal
      v-model="showSaverModal"
      no-padding
    >
      <ChecklistSaverModal
        :dossier-template="dossierTemplate"
        :current-checklist="currentChecklist"
        :entity-external-id="entityExternalId"
        :has-checklist="hasChecklist || hasActiveSections"
        @close="showSaverModal = false"
      />
    </StakeModal>

    <StakeModal
      v-model="showLoaderModal"
      no-padding
    >
      <ChecklistLoaderModal
        :entity-external-id="entityExternalId"
        :has-checklist="hasChecklist || hasActiveSections"
        @close="showLoaderModal = false"
        @checklistLoaded="handleChecklistLoaded"
      />
    </StakeModal>
  </div>
</template>

<script setup>
import Vue, { computed, onMounted, ref } from 'vue';
import { v4 as uuid } from 'uuid';
import { ToastProgrammatic as Toast } from 'buefy';
import { prettifyMixedCase } from '@frontend/shared/Util.js';
import {
  Button,
  Buttons,
  LoaderWithText,
  Stack,
  StakeModal,
  Toggle,
  Title,
  YesNoBadge,
} from '@stakeui';
import ChecklistSaverModal from '../components/ChecklistSaverModal.vue';
import ChecklistLoaderModal from '../components/ChecklistLoaderModal.vue';
import DocumentPortalSection from '../components/DocumentPortalSection.vue';
import { useClientPortalStore } from '@frontend/ui/stores/clientPortalStore';

const clientPortalStore = useClientPortalStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    default: '',
  },
});

const csrfToken = ref('');
const originalChecklist = ref(null); // baseline (immutable in UI)
const currentChecklist = ref(null); // editable copy
const dossierTemplate = ref({});
const hasFetched = ref(false);
const isFetching = ref(false);
const isSaving = ref(false);
const showSaverModal = ref(false);
const showLoaderModal = ref(false);

// Utilities

// Safe deep clone (prefers structuredClone if available)
const deepClone = obj =>
  typeof structuredClone === 'function' ?
    structuredClone(obj) :
    JSON.parse(JSON.stringify(obj));


// API
const fetchChecklist = async () => {
  isFetching.value = true;

  try {
    const response = await fetch(
        `/api/v1/client-portal/checklists/${props.entityExternalId}/`,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken.value,
          },
        },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch client checklist: ${response.status}`);
    }

    const data = await response.json();
    console.log('Fetched client checklist:', data);

    originalChecklist.value = deepClone(data);
    currentChecklist.value = deepClone(data);
  } catch (err) {
    console.error(`Error fetching client checklist: ${err.message}`);
    hasFetched.value = true;
  } finally {
    isFetching.value = false;
  }
};

const fetchDossierTemplate = async () => {
  isFetching.value = true;

  try {
    const response = await fetch(
        `/api/v1/dossier/template/`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch dossier template: ${response.status}`);
    }

    const data = await response.json();
    console.log('Fetched dossier template:', data);

    dossierTemplate.value = data;
  } catch (err) {
    console.error(`Error fetching dossier template: ${err.message}`);
  } finally {
    isFetching.value = false;
    hasFetched.value = true;
  }
};

const bulkUpdateChecklist = async () => {
  if (operations.value.length === 0) {
    alert('No changes to save');
    return;
  }

  isSaving.value = true;

  try {
    const response = await fetch('/api/v1/client-portal/checklist-items/bulk-update/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken.value,
      },
      body: JSON.stringify({
        entity_id: props.entityExternalId,
        operations: operations.value,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to save changes: ${response.status}`);
    }

    // Server returns nothing -> refetch the canonical checklist
    await fetchChecklist();

    Toast.open({
      message: `Changes saved successfully.`,
      type: 'is-success',
    });
  } catch (err) {
    console.error(`Error saving changes: ${err.message}`);

    Toast.open({
      message: `There was an error saving your changes.`,
      type: 'is-danger',
    });
  } finally {
    isSaving.value = false;
  }
};

// Handlers
const handleSectionToggle = (next, sectionKey) => {
  // If the next value is true, it means the section is going from disabled to enabled
  if (next) {
    handleAddNewSection(sectionKey);
  } else {
    handleRemoveSection(sectionKey);
  }
};

const handleAddNewSection = newSectionKey => {
  const name = newSectionKey.trim();
  if (!name) return;

  if (!name || !currentChecklist.value?.sections) {
    return;
  }

  const sections = currentChecklist.value?.sections ?? {};
  const keyExists = Object.keys(sections).some(
      key => key.toLowerCase() === name.toLowerCase(),
  );

  const displayNameExists = Object.values(sections).some(
      section => section.display_name.toLowerCase() === name.toLowerCase(),
  );

  if (keyExists || displayNameExists) {
    return window.alert('Section already exists');
  }

  Vue.set(currentChecklist.value.sections, name, {
    completed_count: 0,
    display_name: name,
    items: [],
    total_count: 0,
  });
};

const handleRemoveSection = sectionKey => {
  if (!currentChecklist.value?.sections) return;

  const confirmDelete = window.confirm(`Remove section "${prettifyMixedCase(sectionKey)}"?`);
  if (!confirmDelete) return;

  Vue.delete(currentChecklist.value.sections, sectionKey);
};

const handleItemRequirement = (sectionKey, itemId) => {
  const section = currentChecklist.value?.sections?.[sectionKey];
  if (!section) return false;

  const item = section.items.find(i => i.id === itemId);
  if (!item) return false;

  item.is_required = !item.is_required;
  return true;
};

const handleItemAddition = (sectionKey, documentType, customDocumentName) => {
  // Ensure checklist and sections exist
  if (!currentChecklist.value) return null;
  if (!currentChecklist.value.sections) {
    Vue.set(currentChecklist.value, 'sections', {});
  }

  // Ensure the section exists (create if missing)
  if (!currentChecklist.value.sections[sectionKey]) {
    Vue.set(currentChecklist.value.sections, sectionKey, {
      completed_count: 0,
      display_name: sectionKey,
      items: [],
      total_count: 0,
    });
  }

  const section = currentChecklist.value.sections[sectionKey];

  // Create new item
  const newItem = {
    id: uuid(), // new local id
    completed_at: null,
    custom_document_name: null,
    document: null,
    document_type: null,
    document_type_id: null,
    is_required: false,
    notes: '',
    status: 'pending',
  };

  if (documentType) {
    newItem.document_type = documentType.name;
    newItem.document_type_id = documentType.id;
  } else if (customDocumentName) {
    newItem.custom_document_name = customDocumentName;
  }

  // Add the new item reactively
  section.items.push(newItem);

  // Optional: update total count
  section.total_count = section.items.length;

  return newItem;
};

const handleItemDeletion = (sectionKey, itemId) => {
  if (!window.confirm('Are you sure you want to remove this document type?')) {
    return;
  }

  const section = currentChecklist.value?.sections?.[sectionKey];
  if (!section) return false;

  const idx = section.items.findIndex(i => i.id === itemId);
  if (idx === -1) return false;

  section.items.splice(idx, 1);
  return true;
};

const handleChecklistLoaded = async () => {
  showLoaderModal.value = false;
  await fetchChecklist();
};

const mapById = checklist => {
  const map = new Map();
  Object.entries(checklist.sections || {}).forEach(([sectionKey, section]) => {
    (section.items || []).forEach(item => {
      map.set(String(item.id), { section: sectionKey, item });
    });
  });
  return map;
};

// Computed
const allDossierSections = computed(() => {
  const props = dossierTemplate.value?.schema?.properties || {};

  return Object.keys(props)
      .map(key => ({
        label: prettifyMixedCase(key),
        value: key,
      }));
});

const operations = computed(() => {
  const FIELDS = [
    'document_type_id',
    'document_type',
    'custom_document_name',
    'is_required',
  ];

  const operations = [];

  const origMap = mapById(originalChecklist.value);
  const currMap = mapById(currentChecklist.value);

  const origSectionKeys = Object.keys(originalChecklist.value?.sections || {});
  const currSectionKeys = Object.keys(currentChecklist.value?.sections || {});

  // Sections Additions + Deletions
  for (const key of currSectionKeys) {
    if (!origSectionKeys.includes(key)) {
      operations.push({
        action: 'create_section',
        section_key: key,
        display_name:
          currentChecklist.value.sections[key]?.display_name ??
          prettifyMixedCase?.(key),
      });
    }
  }

  for (const key of origSectionKeys) {
    if (!currSectionKeys.includes(key)) {
      operations.push({
        action: 'delete_section',
        section_key: key,
      });
    }
  }

  // Document Deletions + Updates
  for (const [id, { item: originalItem }] of origMap.entries()) {
    const curr = currMap.get(id);

    if (!curr) {
      operations.push({ action: 'delete', item_id: id });
      continue;
    }

    const { item: currentItem } = curr;
    const changed = {};

    for (const k of FIELDS) {
      const a = originalItem[k] ?? null;
      const b = currentItem[k] ?? null;
      if (a !== b) changed[k] = b;
    }

    if (Object.keys(changed).length) {
      operations.push({ action: 'update', item_id: id, ...changed });
    }
  }

  // Document Creations
  for (const [id, { section: cSec, item: currentItem }] of currMap.entries()) {
    if (!origMap.has(id)) {
      const create = { action: 'create', section_key: cSec };

      for (const k of FIELDS) {
        if (currentItem[k] !== undefined && currentItem[k] !== null) create[k] = currentItem[k];
      }

      operations.push(create);
    }
  }

  return operations;
});

const hasChecklist = computed(() => {
  return !!currentChecklist.value?.checklist;
});

const hasActiveSections = computed(() => {
  const sections = currentChecklist.value?.sections;
  if (!sections) return false;

  // Get all section keys except 'other'
  const activeKeys = Object.keys(sections).filter(key => key.toLowerCase() !== 'other');

  return activeKeys.length > 0;
});
const hasOperations = computed(() => operations.value.length > 0);

// Lifecycle
onMounted(async () => {
  csrfToken.value = document.querySelector(
      '[name="csrfmiddlewaretoken"]',
  )?.value;
  await fetchChecklist();
  await fetchDossierTemplate();
});
</script>

<style scoped>
.checklist-view {
  padding-bottom: 20rem;
}

.checklist-empty {
  overflow: hidden;
  position: relative;
}

.checklist-empty-box {
  background-color: #fff;
  box-shadow: var(--box-inner-shadow);
  border-radius: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2em;
  flex-grow: 1;
}

.checklist-empty-body {
  position: relative;
}

.add-line {
  max-width: 40em;
  width: 100%;
}

.sections-list {
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity;

  &.is-fetching {
    opacity: 0.5;
  }
}

.save-footer {
  padding: 2em 1em;
  margin: -1em;
  z-index: 2;

  &.is-sticky {
    position: sticky;
    bottom: 0;
  }
}

.save-footer-disclaimer {
  text-align: center;
  color: var(--stk-text-weak);
  margin-top: -1.5em;
}

.or {
  display: flex;
  align-items: center;
  gap: 1em;
  text-align: center;
  text-transform: uppercase;
  font-size: 0.75em;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: var(--stk-text-weak);

  &::before,
  &::after {
    background-color: var(--stk-border);
    content: "";
    display: block;
    height: 1px;
    flex-grow: 1;
  }
}
</style>
